<template>
  <div class="student-tab-class">
    <div v-if="dataTable.length && hasTableData">
      <md-card
        class="card-content my-20"
        v-for="(data, index) in dataTable"
        :key="data.id + data.name + index"
      >
        <md-card-header class="md-card-header-text md-card-header-green">
          <div class="card-text">
            <h4 class="title text-lg">{{ data.name }}</h4>
          </div>
        </md-card-header>
        <md-card-content>
          <div class="text-right flex">
            <div class="w-1/3">
              <md-field>
                <label class="select-label">{{ $t("common.source") }}</label>
                <md-select
                  v-model="data.lmsSystem"
                  @md-selected="selectLmsSystem(index, data.lmsSystem)"
                >
                  <md-option
                    v-for="(lmsItem, lmsIndex) in lmsOptions"
                    :key="lmsIndex + 'lmsSelect'"
                    :value="lmsItem.id"
                  >
                    {{ $t(lmsItem.name) }}
                  </md-option>
                </md-select>
              </md-field>
            </div>
          </div>
          <div v-if="data.body.length && data.changedData">
            <yv-table
              v-if="data.lmsSystem && canEditStudentInfo()"
              :tableData="data.body"
              :pagination="pagination"
              :columns="columns"
              :hideAction="true"
              :hideBulkAction="false"
              :bulkActions="renderBulkAction(data.lmsSystem, index)"
              :sort-data="sortData"
              selectable="multiple"
              @selectItems="items => onSelectItems(index, items)"
              :selectedItems="data.selectedItems"
            >
            </yv-table>
            <yv-table
              v-else
              :tableData="data.body"
              :pagination="pagination"
              :columns="columns"
              :hideAction="true"
              :hideBulkAction="true"
              :sort-data="sortData"
            >
            </yv-table>
          </div>
          <no-data-found v-else></no-data-found>
        </md-card-content>
      </md-card>
    </div>
    <md-card v-else>
      <md-card-content>
        <no-data-found></no-data-found>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import NoDataFound from "@/components/Base/Illustration/NoDataFound";
import { service_paths } from "@/constants/Services";
import YvTable from "@/components/Base/YvTable";
import { DEFAULT_OPTION_ALL, TEACHER_ASSIGNMENTS } from "@/constants";
import cloneDeep from "lodash/cloneDeep";
import Swal from "sweetalert2";
import { getId, initLoader, Message } from "@/helpers/utils";
import { Student } from "@/constants/Permissions";
import { hasValidRole } from "@/helpers/role-helper";
import { mapGetters } from "vuex";
import Role from "@/constants/Role";

export default {
  name: "class",
  components: {
    YvTable,
    NoDataFound
  },
  props: ["isActiveTab", "studentData"],
  data() {
    const studentId = this.$route.params.id;
    return {
      path: `${service_paths.students}/${studentId}/classes`,
      primaryData: [],
      dataTable: [],
      isActive: false,
      columns: {
        className: "student.class",
        subject: "common.subject",
        primaryTeacher: "student.primary_teacher",
        status: "common.status",
        credits: "student.credits",
        weight: "student.weight",
        source: "common.source"
      },
      pagination: {
        hidePerPageOptions: true
      },
      sortData: {
        currentSort: "id",
        currentSortOrder: "desc"
      },
      lmsOptions: [],
      hasTableData: false
    };
  },
  watch: {
    isActiveTab: function(newVal) {
      if (this.isActive) return;
      if (newVal) {
        this.fetchDataClass();
        this.isActive = true;
      }
    }
  },
  created() {
    this.getLMSOptions();
  },
  computed: {
    ...mapGetters("user", ["role"])
  },
  methods: {
    canEditStudentInfo() {
      return (
        this.$can(Student.actions.edit, Student.subject) &&
        !hasValidRole(this.role, Role.Student)
      );
    },
    renderBulkAction(lmsSystem, index) {
      let tableBulkActions = [
        {
          content: "class.un_assign",
          action: () => {
            this.confirmUnassign(index);
          }
        }
      ];
      if (lmsSystem === "agilix") {
        tableBulkActions.push(
          {
            content: "student.withdrawal",
            action: () => {
              this.setAssignmentStatus(index, "withdrawal");
            }
          },
          {
            content: "common.active",
            action: () => {
              this.setAssignmentStatus(index, "Active");
            }
          },
          {
            content: "common.inactive",
            action: () => {
              this.setAssignmentStatus(index, "Inactive");
            }
          }
        );
      }
      return tableBulkActions;
    },

    confirmUnassign(index) {
      Swal.fire({
        title: this.$t("class.unassign_students"),
        text: this.$t("class.are_you_sure_unassign_selected_students"),
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-success btn-fill",
        cancelButtonClass: "md-button md-danger btn-fill",
        confirmButtonText: this.$t("common.yes"),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.unassignStudents(index);
        }
      });
    },
    setAssignmentStatus(index, action) {
      let body = {
        assignments: this.dataTable[index].selectedItems.map(item => ({
          class_id: getId(item),
          status: action
        }))
      };
      this.$axios
        .post(
          `${service_paths.classes}/student/${getId(
            this.studentData
          )}/assignment-status`,
          body
        )
        .then(() => {
          Message.success(this.$t("common.update_success"));
          this.$emit("reloadData");
        });
    },
    unassignStudents(index) {
      this.$axios
        .post(
          `${service_paths.classes}/student/${getId(
            this.studentData
          )}/un-assign-student`,
          {
            classes: this.dataTable[index].selectedItems.map(item =>
              getId(item)
            )
          }
        )
        .then(() => {
          Message.success(this.$t("common.update_success"));
          this.$emit("reloadData");
        });
    },
    onSelectItems(index, value) {
      this.dataTable[index].selectedItems = value;
    },
    selectLmsSystem(index, value) {
      let loader = initLoader();
      this.dataTable[index].changedData = false;
      if (!value) {
        this.dataTable[index].body = this.primaryData[index].body;
      } else {
        this.dataTable[index].body = this.primaryData[index].body.filter(
          item => {
            return item.lms?.name === value;
          }
        );
      }
      setTimeout(() => {
        this.dataTable[index].changedData = true;
        loader.hide();
      }, 10);
    },
    getLMSOptions() {
      this.$axios.get(service_paths.lms).then(({ data }) => {
        if (data) {
          let listSystem = data.data.map(item => ({
            id: item.name,
            name: item.name
          }));
          this.lmsOptions = [...DEFAULT_OPTION_ALL, ...listSystem];
        }
      });
    },
    fetchDataClass() {
      this.hasTableData = false;
      this.$axios.get(this.path).then(resp => {
        if (resp) {
          this.dataTable = [];
          const classList = resp.data.filter(el => el.classes.length);
          this.getTableData(classList);
          this.hasTableData = true;
        }
      });
    },
    getTableData(dataClass) {
      dataClass.forEach(data => {
        this.dataTable.push({
          name: data.name,
          body: this.generateTableBody(data.classes),
          lmsSystem: "",
          selectedItems: [],
          changedData: true
        });
      });
      this.primaryData = cloneDeep(this.dataTable);
    },
    generateTableBody(classes) {
      let classList = [];
      classes.map((classItem, index) => {
        const linkClassDetail = `/classes/${getId(classItem)}/detail`;
        classList.push({
          noNumb: index + 1,
          class: classItem.name,
          className: `<a href="${linkClassDetail}" target="_blank">${classItem.name}</a>`,
          subject: classItem.subject?.code + " - " + classItem.subject?.name,
          primaryTeacher: this.getPrimaryTeacher(classItem),
          status:
            classItem.status === "Concluded"
              ? `<span style="color: #f44336">${classItem.status}</span>`
              : `<span style="color: #4caf50">${classItem.status}</span>`,
          credits: classItem.subject?.credit,
          weight: classItem.subject?.weight,
          lms: classItem.lms,
          source: classItem.lms?.name,
          id: getId(classItem)
        });
      });
      return classList;
    },

    getPrimaryTeacher(data) {
      const primaryTeacher = data.teachers?.find(
        teacher => teacher.assignment === TEACHER_ASSIGNMENTS.PRIMARY
      );
      return primaryTeacher?.users?.user_no_sql?.full_name || "";
    }
  }
};
</script>

<style lang="scss" scoped>
.select-label {
  top: unset;
  bottom: 36px !important;
  font-size: 14px;
}
</style>
